import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectsService, type ParseDatasetResult } from '../services/projects';
import api from '../services/api';
import { startAutoImport, type ImportStep, type AIPResult } from '../services/aipAutoImportService';
import { aerodromeService, type AerodromeData } from '../services/aerodromeService';
import { useLanguage } from '../context/LanguageContext';
import { fetchNotamsForProject } from '../services/notamService';
import ProjectAdvancedConfig, {
  buildSmeConfig,
  emptyAdvancedConfig,
  applyParseResult,
  type AdvancedConfigValue,
} from '../components/project/ProjectAdvancedConfig';
// NotamFetchResult type available from notamService if needed for future NOTAM display
import {
  Plane,
  Database,
  Plus,
  Search,
  Save,
  XCircle,
  ArrowRightLeft,
  Building2,
  Ruler,
  ChevronDown,
  ChevronUp,
  CloudSun,
  Download,
  Check,
  Loader2,
  Calendar,
  AlertTriangle,
  FileText,
  Upload,
  Zap,
  Settings2
} from 'lucide-react';
import {
  CheckCircle as PhCheckCircle,
  XCircle as PhXCircle,
  CircleNotch,
  Lightning,
} from '@phosphor-icons/react';

interface Airport { iata: string; icao: string; name: string; }

/**
 * Internal marker prefixed to `metarProgress` to flag it as an error, so the
 * banner can style it without a status glyph in the text (this project forbids
 * emoji). Derive everything from the constant — a hardcoded slice length is an
 * off-by-one waiting to happen.
 */
const METAR_ERR = '__ERR__';
const isMetarError = (msg: string) => msg.startsWith(METAR_ERR);
const metarProgressText = (msg: string) =>
  isMetarError(msg) ? msg.slice(METAR_ERR.length) : msg;

interface RunwayPair {
  id: number;
  designatorA: string;
  designatorB: string;
  // RunCap infrastructure per runway
  length_m: number;
  rapid_exit_taxiways: number;
  holding_points: number;
  ils_category: 'I' | 'II' | 'IIIA' | 'IIIB';
}

const NumberInput = ({ label, val, set, unit, placeholder }: { label: string; val: number | string; set: (v: number) => void; unit?: string; placeholder?: string }) => (
  <div className="mb-3">
    <label className="block text-xs mb-1 font-bold uppercase tracking-wide text-[var(--text-secondary)]">{label}</label>
    <div className="flex items-center border rounded focus-within:border-[var(--aipron-accent)] transition-colors bg-[var(--surface-raised)] border-[var(--border)]">
      <input
        type="number"
        className="w-full bg-transparent p-2 text-sm outline-none text-[var(--text-primary)] font-mono tabular-nums"
        value={val || ''}
        onChange={e => set(parseFloat(e.target.value))}
        placeholder={placeholder}
        onWheel={(e) => e.currentTarget.blur()}
      />
      {unit && <span className="text-xs pr-2 select-none text-[var(--text-tertiary)]">{unit}</span>}
    </div>
  </div>
);

export const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  // --- DESIGN TOKENS (theme-agnostic — CSS variables switch automatically) ---
  const bgPage = 'bg-[var(--bg)]';
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';
  const cardBg = 'bg-[var(--surface)] border-[var(--border)]';
  const inputBg = 'bg-[var(--surface-raised)] border-[var(--border)] text-[var(--text-primary)]';

  // Datos Aeropuerto
  const [name, setName] = useState('');
  const [iata, setIata] = useState('');
  const [icao, setIcao] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [showResults, setShowResults] = useState(false);
  const isSelectionRef = useRef(false);

  // Estado UI
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Infraestructura
  const [numRunways, setNumRunways] = useState(1);
  const [runwayPairs, setRunwayPairs] = useState<RunwayPair[]>([
    { id: 1, designatorA: '', designatorB: '', length_m: 3500, rapid_exit_taxiways: 2, holding_points: 2, ils_category: 'I' }
  ]);

  // Datos Base (simplified — RRC and PTBC auto-computed)
  const [baseStats, setBaseStats] = useState({
    annual_passengers: 0,
    annual_movements: 0,
  });

  // Auto-estimate movements from PAX (default ratio ~6.5 pax/mov)
  const handlePaxChange = (pax: number) => {
    const estimatedMov = pax > 0 ? Math.round(pax / 6.5) : 0;
    setBaseStats(prev => ({
      ...prev,
      annual_passengers: pax,
      annual_movements: prev.annual_movements === 0 || prev.annual_movements === Math.round((prev.annual_passengers || 0) / 6.5)
        ? estimatedMov : prev.annual_movements,
    }));
  };

  // CNS config — auto-populated from AIP, kept in state for payload
  const [cnsConfig, setCnsConfig] = useState({
    smgcs_type: 'NONE' as 'NONE' | 'SMGCS' | 'A-SMGCS',
    asmgcs_level: 1,
    has_adsb: false,
    has_mlat: false,
    comm_coverage_pct: 100,
    nav_coverage_pct: 100,
    surv_coverage_pct: 100,
  });

  // D-FOL Inputs (manual — field observation data from tower visits)
  const [dfolInputs, setDfolInputs] = useState({
    w_workload: 2.74,
    n_dynamic: 1.5,
    t_sector: 12.5,
  });

  // AirCap terminal config + advanced DACAMS params (CF/IPS weights, v_ref).
  // Everything left blank is omitted from the payload so the backend applies its
  // own documented fallback (ADRM defaults / AODB auto-derived weights).
  const [advConfig, setAdvConfig] = useState<AdvancedConfigValue>(emptyAdvancedConfig());

  // --- DATASET (AODB) — uploaded IN the wizard so the AirCap card pre-fills. ---
  // The SAME File is parsed here (stateless, pre-fill) and, after creation, sent
  // to /{id}/upload to launch the pipeline (Pablo 2026-08-04, UX-1).
  const [datasetFile, setDatasetFile] = useState<File | null>(null);
  const [datasetParsing, setDatasetParsing] = useState(false);
  const [datasetError, setDatasetError] = useState('');
  const [parseResult, setParseResult] = useState<ParseDatasetResult | null>(null);
  // Are the datasheet's times UTC or already airport-local? Cannot be inferred
  // from the values; declared here (default UTC, Pablo 2026-07-17).
  const [datasetTimeBasis, setDatasetTimeBasis] = useState<'utc' | 'local'>('utc');

  const handleDatasetUpload = async (file: File) => {
    setDatasetFile(file);
    setDatasetParsing(true);
    setDatasetError('');
    setParseResult(null);
    try {
      const res = await projectsService.parseDataset(file);
      setParseResult(res);
      // Merge the auto-built terminal config into the (editable) advanced config.
      setAdvConfig(prev => applyParseResult(prev, res));
    } catch (e: unknown) {
      const detail = (e as Record<string, Record<string, Record<string, string>>>)?.response?.data?.detail
        || (language === 'es' ? 'No se pudo parsear el dataset' : 'Could not parse the dataset');
      setDatasetError(detail);
    } finally {
      setDatasetParsing(false);
    }
  };

  // --- METAR/TAF (Weather Data for WCF) ---
  const [metarEnabled, setMetarEnabled] = useState(false);
  const [metarStartDate, setMetarStartDate] = useState('');
  const [metarEndDate, setMetarEndDate] = useState('');
  const [metarFetching, setMetarFetching] = useState(false);
  const [metarProgress, setMetarProgress] = useState('');
  const [metarResult, setMetarResult] = useState<{
    file_path: string;
    metar_count: number;
    taf_count: number;
    raw_size_kb: number;
  } | null>(null);

  const handleFetchMetar = async () => {
    if (!icao || !metarStartDate || !metarEndDate) return;
    setMetarFetching(true);
    setMetarProgress(language === 'es' ? 'Descargando METARs...' : 'Downloading METARs...');
    setMetarResult(null);
    try {
      const res = await api.post('/weather/fetch-metar', {
        icao,
        start_date: metarStartDate,
        end_date: metarEndDate,
      }, { timeout: 600000 }); // 10 minutes — IEM downloads 12 chunks for a year
      setMetarResult(res.data);
      setMetarProgress('');
    } catch (e: unknown) {
      const detail = (e as Record<string, Record<string, Record<string, string>>>)?.response?.data?.detail || (language === 'es' ? 'Error al descargar datos' : 'Error downloading data');
      // Internal-only sentinel (stripped before render) — marks this progress
      // string as an error so the banner below can style it accordingly
      // without embedding a status glyph in the displayed text.
      setMetarProgress(`${METAR_ERR}${detail}`);
    } finally {
      setMetarFetching(false);
    }
  };

  // --- NOTAM Configuration ---
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const nextWeekStr = nextWeek.toISOString().split('T')[0];

  const [notamEnabled, setNotamEnabled] = useState(false);
  const [notamStartDate, setNotamStartDate] = useState(todayStr);
  const [notamEndDate, setNotamEndDate] = useState(nextWeekStr);
  // notamResult state removed — reserved for future NOTAM display integration
  const [notamToast, setNotamToast] = useState<{ type: 'success' | 'error' | 'warning'; message: string } | null>(null);

  // --- AIP AD2 Upload ---
  const [aipFile, setAipFile] = useState<File | null>(null);
  const [aipParsing, setAipParsing] = useState(false);
  const [aipResult, setAipResult] = useState<{
    airport_name: string;
    icao_code: string;
    runways: Record<string, any>[];
    taxiways: Record<string, any>[];
    cns: Record<string, any>;
    max_aircraft_pcn: string;
    confidence: number;
    summary: Record<string, any>;
    parsed_sections: string[];
  } | null>(null);
  const [aipError, setAipError] = useState('');
  const [showAipDetails, setShowAipDetails] = useState(false);

  // --- Auto-Import AIP ---
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAirport, setPendingAirport] = useState<Airport | null>(null);
  const [autoImporting, setAutoImporting] = useState(false);
  const [importSteps, setImportSteps] = useState<ImportStep[]>([]);
  const [showManualUpload, setShowManualUpload] = useState(false);
  const cancelImportRef = useRef<(() => void) | null>(null);

  const handleAipUpload = async (file: File) => {
    setAipFile(file);
    setAipParsing(true);
    setAipResult(null);
    setAipError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/aip/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600000, // 10 min — large AIPs (20MB+) need extended parsing time
      });
      const data = res.data;
      setAipResult(data);

      // Auto-fill runway pairs
      if (data.runways && data.runways.length > 0) {
        const newPairs = data.runways.map((rwy: Record<string, unknown>, idx: number) => ({
          id: idx + 1,
          designatorA: rwy.designator_a || '',
          designatorB: rwy.designator_b || '',
          length_m: rwy.length_m || 0,
          rapid_exit_taxiways: rwy.rapid_exit_taxiways || 1,
          holding_points: rwy.holding_points || 1,
          ils_category: rwy.ils_category || 'I',
        }));
        setRunwayPairs(newPairs);
        setNumRunways(newPairs.length);
      }

      // Auto-fill CNS config
      if (data.cns) {
        setCnsConfig(prev => ({
          ...prev,
          smgcs_type: data.cns.smgcs_type || prev.smgcs_type,
          asmgcs_level: data.cns.asmgcs_level || prev.asmgcs_level,
          has_adsb: data.cns.has_adsb ?? prev.has_adsb,
          has_mlat: data.cns.has_mlat ?? prev.has_mlat,
        }));
      }

    } catch (e: unknown) {
      const detail = (e as Record<string, Record<string, Record<string, string>>>)?.response?.data?.detail || (language === 'es' ? 'Error al parsear el PDF' : 'Error parsing PDF');
      setAipError(detail);
    } finally {
      setAipParsing(false);
    }
  };

  // --- BUSCADOR ---
  useEffect(() => {
    if (isSelectionRef.current) {
      isSelectionRef.current = false;
      return;
    }
    const timer = setTimeout(async () => {
      if (searchTerm.length >= 2) {
        try {
          const res = await api.get<Airport[]>(`/airports?q=${searchTerm}`);
          const uniqueAirports = Array.from(new Map(res.data.map(item => [item.iata, item])).values());
          setResults(uniqueAirports);
          setShowResults(uniqueAirports.length > 0);
        } catch {
          setResults([]);
        }
      } else {
        setShowResults(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const selectAirport = (a: Airport) => {
    // Show confirmation modal instead of immediately selecting
    setPendingAirport(a);
    setShowConfirmModal(true);
    setShowResults(false);
  };

  const handleCancelAirport = () => {
    setShowConfirmModal(false);
    setPendingAirport(null);
  };

  const applyAipData = useCallback((data: AIPResult) => {
    // Auto-fill runway pairs
    if (data.runways && data.runways.length > 0) {
      const newPairs = data.runways.map((rwy: Record<string, unknown>, idx: number) => ({
        id: idx + 1,
        designatorA: (rwy.designator_a as string) || '',
        designatorB: (rwy.designator_b as string) || '',
        length_m: (rwy.length_m as number) || 0,
        rapid_exit_taxiways: (rwy.rapid_exit_taxiways as number) || 1,
        holding_points: (rwy.holding_points as number) || 1,
        ils_category: ((rwy.ils_category as string) || 'I') as 'I' | 'II' | 'IIIA' | 'IIIB',
      }));
      setRunwayPairs(newPairs);
      setNumRunways(newPairs.length);
    }
    // Auto-fill CNS config
    if (data.cns) {
      setCnsConfig(prev => ({
        ...prev,
        smgcs_type: (data.cns.smgcs_type as 'NONE' | 'SMGCS' | 'A-SMGCS') || prev.smgcs_type,
        asmgcs_level: (data.cns.asmgcs_level as number) || prev.asmgcs_level,
        has_adsb: (data.cns.has_adsb as boolean) ?? prev.has_adsb,
        has_mlat: (data.cns.has_mlat as boolean) ?? prev.has_mlat,
      }));
    }
  }, []);

  /**
   * Convert AerodromeData from the BD into the AIPResult shape used by the form.
   */
  const convertBdToAipResult = useCallback((bd: AerodromeData): AIPResult => {
    return {
      airport_name: bd.name || '',
      icao_code: bd.icao_code,
      runways: bd.runways || [],
      taxiways: bd.taxiways || [],
      cns: bd.cns_data || {},
      max_aircraft_pcn: bd.max_aircraft_pcn || '',
      confidence: bd.confidence || 0,
      summary: {
        runways_found: (bd.runways || []).length,
        taxiways_found: (bd.taxiways || []).length,
        stands_found: (bd.stands || []).length,
        has_ils: (bd.runways || []).some((r: any) => r.ils_a || r.ils_b || r.ils_category),
        has_adsb: (bd.cns_data as any)?.has_adsb || false,
        max_pcn: bd.max_aircraft_pcn || '—',
      },
      parsed_sections: bd.parsed_sections || [],
    };
  }, []);

  const handleConfirmAirport = useCallback(async () => {
    if (!pendingAirport) return;
    const a = pendingAirport;

    // Apply the airport selection
    isSelectionRef.current = true;
    setIata(a.iata);
    setIcao(a.icao);
    setName(language === 'es' ? `Análisis - ${a.name}` : `Analysis - ${a.name}`);
    setSearchTerm(a.name);
    setShowConfirmModal(false);

    // Reset state
    setAutoImporting(true);
    setImportSteps([]);
    setAipResult(null);
    setAipError('');
    setAipFile(null);
    setShowManualUpload(false);

    // ── BD-FIRST: Check aerodrome database before SSE import ──
    try {
      const bdData = await aerodromeService.get(a.icao);

      if (bdData && bdData.data_source !== 'none' && bdData.confidence > 0) {
        // Aerodrome found in BD with data — instant apply!
        const isFullyEnriched = bdData.data_source === 'aip_pdf' || bdData.data_source === 'aip_html';
        console.log(`[AIP Agent] Found ${a.icao} in BD (source: ${bdData.data_source}, confidence: ${Math.round(bdData.confidence * 100)}%${isFullyEnriched ? ' — fully enriched' : ' — basic data, upload available'})`);

        const aipData = convertBdToAipResult(bdData);

        // Show instant-complete step
        setImportSteps([{
          step: 1,
          total: 1,
          status: 'done',
          message_es: isFullyEnriched
            ? `Datos completos cargados desde la base de datos (AIP AD2 · AIRAC ${bdData.airac_cycle || '—'})`
            : `Datos base cargados (OurAirports). Puedes subir el AIP AD2 para enriquecer con taxiways, stands, PCN e ILS.`,
          message_en: isFullyEnriched
            ? `Complete data loaded from database (AIP AD2 · AIRAC ${bdData.airac_cycle || '—'})`
            : `Base data loaded (OurAirports). You can upload AIP AD2 to enrich with taxiways, stands, PCN and ILS.`,
        }]);

        setAutoImporting(false);
        setAipResult(aipData as any);
        applyAipData(aipData);

        // If only OurAirports data → show manual upload so user can enrich with AIP AD2
        if (!isFullyEnriched) {
          setShowManualUpload(true);
        }
        return;
      }
    } catch (err) {
      // BD check failed — fall through to SSE auto-import
      console.warn('[AIP Agent] BD check failed, falling back to SSE:', err);
    }

    // ── FALLBACK: SSE auto-import (original flow) ──
    const cancel = startAutoImport(
      a.icao,
      (step) => {
        setImportSteps(prev => {
          const existing = prev.findIndex(s => s.step === step.step);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = step;
            return updated;
          }
          return [...prev, step];
        });
      },
      (data) => {
        setAutoImporting(false);
        const castData = data as unknown as {
          airport_name: string;
          icao_code: string;
          runways: Record<string, unknown>[];
          taxiways: Record<string, unknown>[];
          cns: Record<string, unknown>;
          max_aircraft_pcn: string;
          confidence: number;
          summary: Record<string, unknown>;
          parsed_sections: string[];
        };
        setAipResult(castData);
        applyAipData(data);
      },
      (error) => {
        setAutoImporting(false);
        setAipError(error);
        setShowManualUpload(true);
      }
    );

    cancelImportRef.current = cancel;
  }, [pendingAirport, language, applyAipData, convertBdToAipResult]);

  // --- LÓGICA PISTAS ---
  const handleNumRunwaysChange = (n: number) => {
    setNumRunways(n);
    const newPairs = [...runwayPairs];
    if (n > newPairs.length) {
      for (let i = newPairs.length; i < n; i++) newPairs.push({
        id: i + 1, designatorA: '', designatorB: '',
        length_m: 3500, rapid_exit_taxiways: 2, holding_points: 2, ils_category: 'I'
      });
    } else {
      newPairs.splice(n);
    }
    setRunwayPairs(newPairs);
  };

  const calculateReciprocal = (des: string): string => {
    const match = des.match(/^(\d{1,2})([LCRlcr]?)$/);
    if (!match) return '';
    const num = parseInt(match[1]);
    const letter = match[2].toUpperCase();
    if (num < 1 || num > 36) return '';
    const recNum = num > 18 ? num - 18 : num + 18;
    const recLetter = letter === 'L' ? 'R' : letter === 'R' ? 'L' : letter;
    return `${recNum.toString().padStart(2, '0')}${recLetter}`;
  };

  const handleRunwayInput = (index: number, value: string) => {
    const newPairs = [...runwayPairs];
    newPairs[index].designatorA = value.toUpperCase();
    if (value.length >= 2) {
      const rec = calculateReciprocal(value);
      if (rec) newPairs[index].designatorB = rec;
    }
    setRunwayPairs(newPairs);
  };

  const handleReciprocalInput = (index: number, value: string) => {
    const newPairs = [...runwayPairs];
    newPairs[index].designatorB = value.toUpperCase();
    setRunwayPairs(newPairs);
  };

  const updateRunwayField = (index: number, field: keyof RunwayPair, value: string | number) => {
    const newPairs = [...runwayPairs];
    (newPairs[index] as unknown as Record<string, string | number>)[field] = value;
    setRunwayPairs(newPairs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    try {
      // Build runcap_infra from runway pairs
      const runcapInfra = runwayPairs.map(rw => ({
        runway_id: rw.designatorA && rw.designatorB
          ? `${rw.designatorA}/${rw.designatorB}`
          : rw.designatorA || `RWY-${rw.id}`,
        length_m: rw.length_m,
        rapid_exit_taxiways: rw.rapid_exit_taxiways,
        holding_points: rw.holding_points,
        ils_category: rw.ils_category,
      }));

      // RunCap engine config for the PRIMARY runway. Persisted as
      // sme_parameters.runcap_config so the tri-node resolves effRRC from the
      // RunCap ENGINE (resolve_eff_rrc prefers runcap_config over the declared
      // scalar) instead of falling back to runway_capacity.
      //
      // Single source of truth: the same object also drives the post-create RRC
      // auto-calculation below, so the persisted config and the computed scalar
      // can never drift apart.
      //
      // NO aircraft_mix here, deliberately. The real fleet mix comes from the
      // datasheet's ARCTYP column (real AODB-sourced data), not from this form —
      // at creation time no datasheet has been uploaded yet, so any mix we sent
      // would be fabricated. Worse,
      // sending one would look like an explicit user choice and would take
      // precedence over the data-derived mix. Omitting it lets resolve_eff_rrc
      // apply its own documented default until the AODB-derived RECAT-EU
      // distribution feeds the bridge.
      //
      // parallel_runway stays false even for multi-runway airports: the form
      // does not capture runway spacing, and RunCap only grants a parallel bonus
      // from >=760 m. Claiming "parallel" with an invented spacing would fabricate
      // capacity, so we keep today's conservative single-runway basis until
      // spacing is captured (AIP AD2 can supply it).
      const primaryRw = runcapInfra[0];
      const runcapConfig = primaryRw ? {
        runway_id: primaryRw.runway_id,
        length_m: primaryRw.length_m,
        mode: 'mixed' as const,
        weather: 'VMC' as const,
        separation_std: 'RECAT-EU' as const,
        method: 'IATA_ADRM' as const,
        parallel_runway: false,
        parallel_independent: false,
        parallel_spacing_m: 0,
        rot_arrival_s: 50,
        rot_departure_s: 40,
        arr_dep_ratio: 0.5,
        n_rets: primaryRw.rapid_exit_taxiways || 0,
        pct_touch_and_go: 0,
        sam_mean_speed_kt: 130,
        sam_standard_separation_nm: 3,
        sam_rsm_nm: 0.5,
      } : null;

      const payload = {
        name,
        iata_code: iata,
        icao_code: icao,
        analysis_status: 'DRAFT' as const,
        has_file: !!datasetFile,
        created_at: new Date().toISOString(),

        // Simplified — RRC and PTBC computed post-creation
        total_pax_last_year: baseStats.annual_passengers,
        pax_capacity: 0, // PTBC auto-calculated by AirCap
        total_mov_last_year: baseStats.annual_movements,
        runway_capacity: 0, // RRC auto-calculated by RunCap
        runway_config: { count: numRunways, pairs: runwayPairs },

        sme_parameters: {
          // Annual PAX/movements also mirrored into sme_parameters so the
          // post-creation config modal (which reads sme_parameters.pax_anuales)
          // shows what was entered here, and the PTBC pax->mov chain has them.
          ...(baseStats.annual_passengers > 0 ? { pax_anuales: baseStats.annual_passengers } : {}),

          // CNS Config (auto from AIP or defaults)
          smgcs_type: cnsConfig.smgcs_type,
          asmgcs_level: cnsConfig.asmgcs_level,
          has_adsb: cnsConfig.has_adsb,
          has_mlat: cnsConfig.has_mlat,
          comm_coverage_pct: cnsConfig.comm_coverage_pct,
          nav_coverage_pct: cnsConfig.nav_coverage_pct,
          surv_coverage_pct: cnsConfig.surv_coverage_pct,

          // D-FOL — manual field observation data
          w_workload: dfolInputs.w_workload,
          n_dynamic: dfolInputs.n_dynamic,
          t_sector: dfolInputs.t_sector,
          mean_sector_time: dfolInputs.t_sector,

          // RunCap — Runway Infrastructure
          runcap_infra: runcapInfra,

          // RunCap engine config -> tri-node effRRC comes from the RunCap engine
          // (preferred over the declared runway_capacity scalar).
          ...(runcapConfig ? { runcap_config: runcapConfig } : {}),

          // AirCap terminal (aircap_config) + advanced DACAMS params
          // (weight_tcf/wcf/wif/nof/ccf, ips_weight_*, v_ref_mps).
          // Only explicitly-entered values are included.
          ...buildSmeConfig(advConfig),

          // AIP AD2 Data (coordinates, elevation, stands, taxiways)
          ...(aipResult ? {
            aip_data: {
              airport_name: (aipResult as any).airport_name || '',
              icao_code: (aipResult as any).icao_code || icao,
              latitude: (aipResult as any).latitude || '',
              longitude: (aipResult as any).longitude || '',
              elevation_ft: (aipResult as any).elevation_ft || 0,
              stands: (aipResult as any).stands || [],
              taxiways: (aipResult as any).taxiways || [],
              parsed_sections: aipResult.parsed_sections || [],
              confidence: aipResult.confidence || 0,
            }
          } : {}),

          // Weather Data (WCF)
          ...(metarResult ? { metar_file_path: metarResult.file_path } : {}),

          // NOTAM config
          ...(notamEnabled ? {
            notam_config: {
              icao_codes: [icao],
              start_date: notamStartDate,
              end_date: notamEndDate,
            },
          } : {}),
        },
        sectors_config: []
      };

      const newProject = await projectsService.create(payload);

      // If a dataset was supplied in the wizard, upload the SAME File now so the
      // pipeline runs on it (the parse above was stateless pre-fill only). Awaited
      // before navigating so the project opens with ingestion already launched.
      // Dataset is optional: without one, the project is created as a DRAFT and
      // the file can be uploaded later from the project page (flow unchanged).
      if (datasetFile) {
        try {
          await projectsService.uploadFile(newProject.id, datasetFile, datasetTimeBasis);
        } catch (upErr) {
          console.error('[Dataset] upload after create failed:', upErr);
          // Non-fatal: the project exists; the user can retry the upload from the
          // project page. Surface it but still navigate.
          setErrorMsg(language === 'es'
            ? 'Proyecto creado, pero la subida del dataset falló. Podrás reintentarla desde el proyecto.'
            : 'Project created, but the dataset upload failed. You can retry it from the project page.');
        }
      }

      // Non-blocking: auto-calculate the declared RRC scalar via RunCap, from the
      // SAME runcapConfig persisted above (so the two can never diverge).
      if (runcapConfig && primaryRw) {
        const rw = primaryRw;
        import('../services/runcapService').then(({ runcapService }) => {
          runcapService.calculate({
            ...runcapConfig,
            // The endpoint requires a mix; this PRELIMINARY scalar is only a
            // fallback for before the datasheet exists, so it uses the reference
            // distribution. The authoritative effRRC is recomputed in-pipeline
            // from the datasheet-derived mix — it does not depend on this value.
            aircraft_mix: { cat_a: 0, cat_b: 5, cat_c: 25, cat_d: 40, cat_e: 25, cat_f: 5 },
            n_holding_points: rw.holding_points || 1,
            ils_category: rw.ils_category || 'I',
            rapid_exit_taxiways: rw.rapid_exit_taxiways || 0,
            holding_points: rw.holding_points || 1,
          }).then(result => {
            console.log(`[RunCap] Auto-computed RRC: ${result.practical_capacity_per_hour} mov/h`);
            projectsService.update(newProject.id, { runway_capacity: result.practical_capacity_per_hour }).catch(() => { });
          }).catch(err => console.warn('[RunCap] Auto-calc failed:', err));
        });
      }

      // Non-blocking NOTAM fetch
      if (notamEnabled && icao) {
        fetchNotamsForProject([icao], notamStartDate, notamEndDate)
          .then(result => console.log(`[NOTAM] Fetched ${result.notam_count} NOTAMs`))
          .catch(err => console.error('[NOTAM] Fetch failed:', err));
      }

      navigate(`/projects/${newProject.id}`);

    } catch (e: unknown) {
      console.error(e);
      setErrorMsg(language === 'es'
        ? 'No se pudo crear el proyecto. Inténtalo de nuevo.'
        : 'Could not create project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen p-8 font-sans transition-colors duration-300 ${bgPage}`}>
      <div className="w-full max-w-6xl mx-auto">

        <h1 className={`text-3xl font-bold mb-8 flex items-center gap-3 ${textMain}`}>
          <div className="p-2 bg-[var(--aipron-accent)] rounded-lg shadow-lg text-white">
            <Plus className="w-6 h-6" />
          </div>
          {language === 'es' ? 'Nuevo Proyecto de Análisis' : 'New Analysis Project'}
        </h1>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* 1. AEROPUERTO */}
          <div className={`p-6 rounded-xl border ${cardBg}`}>
            <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 text-[var(--aipron-accent)]`}>
              <Plane className="w-5 h-5" /> {language === 'es' ? 'Aeropuerto' : 'Airport'}
            </h2>

            {/* Buscador */}
            <div className="relative mb-6">
              <label className={`block text-xs font-bold mb-2 tracking-wider ${textSub}`}>
                {language === 'es' ? 'BUSCAR AEROPUERTO' : 'SEARCH AIRPORT'}
              </label>
              <div className={`flex items-center border rounded-lg p-2 focus-within:border-[var(--aipron-accent)] transition-colors ${inputBg}`}>
                <Search className="w-4 h-4 text-[var(--text-secondary)] mr-2" />
                <input
                  className={`w-full bg-transparent outline-none text-sm ${textMain}`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={language === 'es' ? 'Ej: Madrid, Heathrow...' : 'E.g.: London, JFK...'}
                />
              </div>

              {showResults && (
                <div className="absolute w-full mt-2 rounded-lg shadow-2xl z-20 max-h-60 overflow-y-auto border bg-[var(--surface-raised)] border-[var(--border)]">
                  {results.map((a, i) => (
                    <div key={i} className="p-3 cursor-pointer border-b flex justify-between items-center hover:bg-[var(--surface-inset)] border-[var(--border-subtle)]" onClick={() => selectAirport(a)}>
                      <span className={`font-bold ${textMain}`}>{a.iata}</span>
                      <span className={`text-sm truncate ml-2 ${textSub}`}>{a.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Datos Seleccionados */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className={`text-xs font-bold ${textSub}`}>
                  {language === 'es' ? 'CÓDIGO IATA' : 'IATA CODE'}
                </label>
                <input value={iata} readOnly className="w-full p-3 rounded-lg font-mono text-center border mt-1 bg-[var(--surface-inset)] border-[var(--border)] text-[var(--text-primary)]" />
              </div>
              <div>
                <label className={`text-xs font-bold ${textSub}`}>
                  {language === 'es' ? 'CÓDIGO OACI' : 'ICAO CODE'}
                </label>
                <input value={icao} readOnly className="w-full p-3 rounded-lg font-mono text-center border mt-1 bg-[var(--surface-inset)] border-[var(--border)] text-[var(--text-primary)]" />
              </div>
            </div>

            <label className={`text-xs font-bold ${textSub}`}>
              {language === 'es' ? 'NOMBRE DEL PROYECTO' : 'PROJECT NAME'}
            </label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              className={`w-full border rounded-lg p-3 text-sm focus:border-[var(--aipron-accent)] outline-none mt-1 ${inputBg}`}
              placeholder={language === 'es' ? 'Nombre identificativo...' : 'Identifying name...'}
            />

            {errorMsg && (
              <div className="mt-4 p-3 rounded-lg flex items-start gap-2 text-xs"
                style={{ background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-danger) 35%, transparent)', color: 'var(--aipron-danger)' }}>
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {errorMsg}
              </div>
            )}
          </div>

          {/* AIP AD2 — AUTO-IMPORT + MANUAL UPLOAD */}
          {icao && (
            <div className={`p-6 rounded-xl border lg:col-span-2 ${cardBg}`}>
              <h2 className={`text-lg font-bold mb-4 flex items-center gap-2 text-[var(--aipron-accent)]`}>
                <Zap className="w-5 h-5" />
                {language === 'es' ? 'Importación AIP AD2' : 'AIP AD2 Import'}
              </h2>

              {/* Auto-import progress pipeline */}
              {(autoImporting || importSteps.length > 0) && !showManualUpload && (
                <div className="rounded-xl border p-5 mb-4 bg-[var(--surface-inset)] border-[var(--border)]">
                  <div className="space-y-3">
                    {importSteps.map((step) => {
                      const statusColor = step.status === 'done' ? 'var(--good)'
                        : step.status === 'error' ? 'var(--crit)'
                          : 'var(--aipron-info)';
                      const msgKey = language === 'es' ? 'message_es' : 'message_en';
                      return (
                        <div key={step.step} className="flex items-start gap-3">
                          <span className="mt-0.5 flex-shrink-0 w-6 flex justify-center" style={{ color: statusColor }}>
                            {step.status === 'done' && <PhCheckCircle size={18} weight="fill" />}
                            {step.status === 'error' && <PhXCircle size={18} weight="fill" />}
                            {step.status === 'running' && <CircleNotch size={18} className="animate-spin" />}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: statusColor }}>
                              {step[msgKey]}
                            </p>
                            {step.status === 'running' && (
                              <div className="mt-2 h-1.5 rounded-full overflow-hidden bg-[var(--surface-raised)]">
                                <div className="h-full bg-[var(--aipron-accent)] rounded-full animate-pulse" style={{ width: '60%' }} />
                              </div>
                            )}
                          </div>
                          <span className={`text-[10px] font-mono ${textSub}`}>
                            {step.step}/{step.total}
                          </span>
                        </div>
                      );
                    })}
                    {autoImporting && importSteps.length === 0 && (
                      <div className="flex items-center gap-3">
                        <Loader2 className={`w-5 h-5 animate-spin text-[var(--aipron-accent)]`} />
                        <span className={`text-sm ${textSub}`}>
                          {language === 'es' ? 'Conectando con el sistema AIP...' : 'Connecting to AIP system...'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Cancel button during import */}
                  {autoImporting && (
                    <button
                      type="button"
                      onClick={() => {
                        cancelImportRef.current?.();
                        setAutoImporting(false);
                        setShowManualUpload(true);
                      }}
                      className="mt-4 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors underline"
                    >
                      {language === 'es' ? 'Cancelar y subir manualmente' : 'Cancel and upload manually'}
                    </button>
                  )}
                </div>
              )}

              {/* Error message */}
              {aipError && (
                <div className="mb-4 p-3 rounded-lg text-xs flex items-center gap-2"
                  style={{ background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-danger) 35%, transparent)', color: 'var(--aipron-danger)' }}>
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  {aipError}
                </div>
              )}

              {/* Manual upload fallback */}
              {(showManualUpload || (!autoImporting && importSteps.length === 0 && !aipResult)) && (
                <>
                  <p className={`text-xs mb-4 ${textSub}`}>
                    {language === 'es'
                      ? 'Sube el documento AIP AD2 (PDF o HTML) y el agente IA extraerá automáticamente pistas, taxiways, stands, CNS/ATM e infraestructura.'
                      : 'Upload the AIP AD2 document (PDF or HTML) and the AI agent will automatically extract runways, taxiways, stands, CNS/ATM, and infrastructure data.'}
                  </p>
                  <label
                    className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-[var(--border)] hover:border-[var(--aipron-accent)] bg-[var(--surface-raised)] ${aipParsing ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <input
                      type="file"
                      accept=".pdf,.html,.htm,.txt"
                      className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (f) handleAipUpload(f);
                      }}
                    />
                    {aipParsing ? (
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 className={`w-8 h-8 animate-spin text-[var(--aipron-accent)]`} />
                        <span className={`text-sm ${textSub}`}>
                          {language === 'es' ? 'El agente IA está leyendo el documento AIP...' : 'AI agent is reading the AIP document...'}
                        </span>
                      </div>
                    ) : aipFile && aipResult ? (
                      <div className="flex flex-col items-center gap-1">
                        <Check className="w-6 h-6" style={{ color: 'var(--good)' }} />
                        <span className="text-sm font-medium" style={{ color: 'var(--good)' }}>
                          {aipFile.name}
                        </span>
                        <span className={`text-xs ${textSub}`}>
                          {language === 'es' ? 'Clic para reemplazar' : 'Click to replace'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload className={`w-8 h-8 ${textSub}`} />
                        <span className={`text-sm font-medium ${textSub}`}>
                          {language === 'es' ? 'Arrastra el AIP AD2 o haz clic para seleccionar' : 'Drag AIP AD2 or click to select'}
                        </span>
                        <span className={`text-[10px] ${textSub}`}>.PDF · .HTML · .TXT · Max 50MB</span>
                      </div>
                    )}
                  </label>
                </>
              )}

              {/* Success summary + detailed data review */}
              {aipResult && (
                <div className="mt-4 rounded-lg border overflow-hidden"
                  style={{ background: 'color-mix(in srgb, var(--good) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--good) 30%, transparent)' }}>
                  {/* Header with confidence */}
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--good)' }} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-bold" style={{ color: 'var(--good)' }}>
                            {language === 'es'
                              ? `Datos extraídos — confianza ${Math.round(aipResult.confidence * 100)}%`
                              : `Data extracted — confidence ${Math.round(aipResult.confidence * 100)}%`}
                          </p>
                          <button
                            type="button"
                            onClick={() => setShowAipDetails(prev => !prev)}
                            className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition-colors hover:bg-[var(--surface-raised)]"
                            style={{ color: 'var(--good)' }}
                          >
                            {showAipDetails
                              ? (language === 'es' ? 'Ocultar detalle' : 'Hide details')
                              : (language === 'es' ? 'Ver detalle completo' : 'View full details')}
                            {showAipDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                        </div>
                        {/* Quick stats row */}
                        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 text-xs ${textSub}`}>
                          <div><span className="font-bold">{language === 'es' ? 'Pistas' : 'Runways'}:</span> {String(aipResult.summary?.runways_found || 0)}</div>
                          <div><span className="font-bold">{language === 'es' ? 'Calles rodaje' : 'Taxiways'}:</span> {String(aipResult.summary?.taxiways_found || 0)}</div>
                          <div><span className="font-bold">{language === 'es' ? 'Stands' : 'Stands'}:</span> {String(aipResult.summary?.stands_found || 0)}</div>
                          <div><span className="font-bold">{language === 'es' ? 'Secciones' : 'Sections'}:</span> {aipResult.parsed_sections?.length || 0}/8</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {showAipDetails && (
                    <div className="border-t px-4 pb-4 pt-3 space-y-4"
                      style={{ borderColor: 'color-mix(in srgb, var(--good) 20%, transparent)', background: 'var(--surface-inset)' }}>

                      {/* Parsed sections */}
                      <div>
                        <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>
                          {language === 'es' ? 'Secciones ICAO parseadas' : 'Parsed ICAO sections'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(aipResult.parsed_sections || []).map((s: string, i: number) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full font-mono border"
                              style={{ background: 'color-mix(in srgb, var(--aipron-info) 12%, transparent)', color: 'var(--aipron-info)', borderColor: 'color-mix(in srgb, var(--aipron-info) 30%, transparent)' }}>
                              {s}
                            </span>
                          ))}
                          {(!aipResult.parsed_sections || aipResult.parsed_sections.length === 0) && (
                            <span className={`text-[10px] ${textSub}`}>—</span>
                          )}
                        </div>
                      </div>

                      {/* Runways table */}
                      {aipResult.runways && aipResult.runways.length > 0 && (
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${textSub}`}>
                            {language === 'es' ? 'Pistas extraídas' : 'Extracted runways'}
                          </p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border-collapse" style={{ color: 'var(--text-secondary)' }}>
                              <thead>
                                <tr className="bg-[var(--surface-raised)]">
                                  <th className="text-left p-1.5 font-bold">{language === 'es' ? 'Designador' : 'Designator'}</th>
                                  <th className="text-right p-1.5 font-bold">{language === 'es' ? 'Longitud' : 'Length'}</th>
                                  <th className="text-right p-1.5 font-bold">{language === 'es' ? 'Ancho' : 'Width'}</th>
                                  <th className="text-left p-1.5 font-bold">{language === 'es' ? 'Superficie' : 'Surface'}</th>
                                  <th className="text-left p-1.5 font-bold">PCN</th>
                                  <th className="text-center p-1.5 font-bold">ILS</th>
                                  <th className="text-right p-1.5 font-bold">TORA</th>
                                  <th className="text-right p-1.5 font-bold">LDA</th>
                                </tr>
                              </thead>
                              <tbody>
                                {aipResult.runways.map((rwy: Record<string, unknown>, i: number) => (
                                  <tr key={i} className="border-t border-[var(--border-subtle)]">
                                    <td className="p-1.5 font-mono font-bold">{String(rwy.designator || `${rwy.designator_a || ''}/${rwy.designator_b || ''}`)}</td>
                                    <td className="p-1.5 text-right font-mono">{rwy.length_m ? `${Number(rwy.length_m).toLocaleString()}m` : '—'}</td>
                                    <td className="p-1.5 text-right font-mono">{rwy.width_m ? `${rwy.width_m}m` : '—'}</td>
                                    <td className="p-1.5">{String(rwy.surface || '—')}</td>
                                    <td className="p-1.5 font-mono">{String(rwy.pcn || '—')}</td>
                                    <td className="p-1.5 text-center">{rwy.ils_category ? `CAT ${rwy.ils_category}` : '—'}</td>
                                    <td className="p-1.5 text-right font-mono">{rwy.tora ? `${Number(rwy.tora).toLocaleString()}m` : '—'}</td>
                                    <td className="p-1.5 text-right font-mono">{rwy.lda ? `${Number(rwy.lda).toLocaleString()}m` : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Taxiways */}
                      {aipResult.taxiways && aipResult.taxiways.length > 0 && (
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>
                            {language === 'es' ? 'Calles de rodaje' : 'Taxiways'} ({aipResult.taxiways.length})
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {aipResult.taxiways.map((twy: Record<string, unknown>, i: number) => (
                              <span key={i} className={`text-[10px] px-2 py-0.5 rounded font-mono border inline-flex items-center gap-0.5 ${twy.is_ret ? 'ring-1 ring-[var(--aipron-warning)]' : ''}`}
                                style={{ background: 'color-mix(in srgb, var(--aipron-warning) 12%, transparent)', color: 'var(--aipron-warning)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 30%, transparent)' }}
                                title={`${twy.designator}${twy.width_m ? ` · ${twy.width_m}m` : ''}${twy.pcn ? ` · PCN ${twy.pcn}` : ''}${twy.is_ret ? ' · RET' : ''}${twy.associated_runway ? ` → ${twy.associated_runway}` : ''}`}
                              >
                                {String(twy.designator)}{twy.is_ret ? <Lightning size={10} weight="fill" /> : ''}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* CNS/ATM + Frequencies row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* CNS/ATM */}
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>CNS/ATM</p>
                          <div className={`grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${textSub}`}>
                            <div className="flex items-center gap-1">ILS: {aipResult.cns?.has_ils ? <PhCheckCircle size={12} weight="fill" style={{ color: 'var(--good)' }} /> : <PhXCircle size={12} weight="fill" style={{ color: 'var(--crit)' }} />}</div>
                            <div className="flex items-center gap-1">ADS-B: {aipResult.cns?.has_adsb ? <PhCheckCircle size={12} weight="fill" style={{ color: 'var(--good)' }} /> : <PhXCircle size={12} weight="fill" style={{ color: 'var(--crit)' }} />}</div>
                            <div className="flex items-center gap-1">MLAT: {aipResult.cns?.has_mlat ? <PhCheckCircle size={12} weight="fill" style={{ color: 'var(--good)' }} /> : <PhXCircle size={12} weight="fill" style={{ color: 'var(--crit)' }} />}</div>
                            <div>SMGCS: {String(aipResult.cns?.smgcs_type || 'NONE')}</div>
                            {aipResult.max_aircraft_pcn && (
                              <div className="col-span-2">Max PCN: <span className="font-mono">{aipResult.max_aircraft_pcn}</span></div>
                            )}
                          </div>
                        </div>

                        {/* Frequencies */}
                        {aipResult.cns?.frequencies && (aipResult.cns.frequencies as string[]).length > 0 && (
                          <div>
                            <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>
                              {language === 'es' ? 'Frecuencias' : 'Frequencies'}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {(aipResult.cns.frequencies as string[]).map((f: string, i: number) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded font-mono border"
                                  style={{ background: 'var(--accent-soft)', color: 'var(--aipron-accent)', borderColor: 'color-mix(in srgb, var(--aipron-accent) 30%, transparent)' }}>
                                  {f} MHz
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Restrictions */}
                      {(aipResult as any).restrictions && (aipResult as any).restrictions.length > 0 && (
                        <div>
                          <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>
                            {language === 'es' ? 'Restricciones' : 'Restrictions'}
                          </p>
                          <div className="space-y-1">
                            {(aipResult as any).restrictions.map((r: { type: string; text: string }, i: number) => (
                              <div key={i} className="text-[10px] p-2 rounded border"
                                style={{ background: 'color-mix(in srgb, var(--aipron-danger) 8%, transparent)', color: 'var(--aipron-danger)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 25%, transparent)' }}>
                                <span className="font-bold uppercase">{r.type}:</span> {r.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 2. INFRAESTRUCTURA & DATOS BASE */}
          <div className={`p-6 rounded-xl border ${cardBg} col-span-full`}>
            <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 text-[var(--aipron-accent)]`}>
              <Database className="w-5 h-5" /> {language === 'es' ? 'Infraestructura & Datos Base' : 'Infrastructure & Base Data'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* COLUMNA A — Runway Config */}
              <div>
                <h3 className="text-sm font-bold mb-4 border-b pb-2 text-[var(--text-primary)] border-[var(--border)]">
                  {language === 'es' ? 'Configuración de Pistas' : 'Runway Configuration'}
                </h3>

                <div className="mb-4">
                  <label className={`text-xs block mb-2 font-bold ${textSub}`}>
                    {language === 'es' ? 'NÚMERO DE PISTAS FÍSICAS' : 'NUMBER OF PHYSICAL RUNWAYS'}
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handleNumRunwaysChange(n)}
                        className={`w-10 h-10 rounded-lg font-bold transition-all ${numRunways === n ? 'bg-[var(--aipron-accent)] text-white shadow-lg scale-105' : 'bg-[var(--surface-raised)] text-[var(--text-tertiary)] border border-[var(--border)] hover:border-[var(--border-strong)]'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Runway Designators Header */}
                <div className="space-y-3">
                  <div className={`grid grid-cols-7 gap-2 text-[10px] uppercase font-bold text-center ${textSub}`}>
                    <span className="col-span-3">{language === 'es' ? 'Cabecera A' : 'Threshold A'}</span>
                    <span className="col-span-1"></span>
                    <span className="col-span-3">{language === 'es' ? 'Cabecera B' : 'Threshold B'}</span>
                  </div>

                  {runwayPairs.map((pair, idx) => (
                    <div key={pair.id} className={`rounded-lg border p-3 ${'border-[var(--border)]'}`}>
                      {/* Designators row */}
                      <div className="grid grid-cols-7 gap-2 items-center">
                        <input
                          className={`col-span-3 border rounded p-2 text-center uppercase font-mono focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                          placeholder={language === 'es' ? 'Ej: 36L' : 'E.g.: 36L'}
                          maxLength={3}
                          value={pair.designatorA}
                          onChange={(e) => handleRunwayInput(idx, e.target.value)}
                        />
                        <div className={`col-span-1 flex justify-center ${textSub}`}>
                          <ArrowRightLeft className="w-4 h-4" />
                        </div>
                        <input
                          className={`col-span-3 border rounded p-2 text-center uppercase font-mono focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                          placeholder={language === 'es' ? 'Ej: 18R' : 'E.g.: 18R'}
                          maxLength={3}
                          value={pair.designatorB}
                          onChange={(e) => handleReciprocalInput(idx, e.target.value)}
                        />
                      </div>

                      {/* RunCap — Per-runway infrastructure */}
                      <div className={`mt-3 pt-3 border-t ${'border-[var(--border)]'}`}>
                        <p className="text-[10px] uppercase font-bold mb-2 flex items-center gap-1" style={{ color: 'var(--node-rrc)' }}>
                          <Ruler className="w-3 h-3" /> RunCap
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={`text-[10px] block mb-1 ${textSub}`}>
                              {language === 'es' ? 'Longitud' : 'Length'}
                            </label>
                            <div className="flex items-center border rounded focus-within:border-[var(--node-rrc)] bg-[var(--surface-raised)] border-[var(--border)]">
                              <input
                                type="number"
                                className={`w-full bg-transparent p-1.5 text-xs outline-none ${'text-[var(--text-primary)]'}`}
                                value={pair.length_m || ''}
                                onChange={e => updateRunwayField(idx, 'length_m', parseFloat(e.target.value) || 0)}
                                onWheel={e => e.currentTarget.blur()}
                              />
                              <span className={`text-[10px] pr-1.5 ${textSub}`}>m</span>
                            </div>
                          </div>
                          <div>
                            <label className={`text-[10px] block mb-1 ${textSub}`}>ILS</label>
                            <select
                              className={`w-full border rounded p-1.5 text-xs outline-none ${inputBg}`}
                              value={pair.ils_category}
                              onChange={e => updateRunwayField(idx, 'ils_category', e.target.value)}
                            >
                              <option value="I">CAT I</option>
                              <option value="II">CAT II</option>
                              <option value="IIIA">CAT IIIA</option>
                              <option value="IIIB">CAT IIIB</option>
                            </select>
                          </div>
                          <div>
                            <label className={`text-[10px] block mb-1 ${textSub}`}>
                              {language === 'es' ? 'RETs' : 'RETs'}
                            </label>
                            <input
                              type="number"
                              min={0}
                              className={`w-full border rounded p-1.5 text-xs outline-none ${inputBg}`}
                              value={pair.rapid_exit_taxiways || ''}
                              onChange={e => updateRunwayField(idx, 'rapid_exit_taxiways', parseInt(e.target.value) || 0)}
                              onWheel={e => e.currentTarget.blur()}
                            />
                          </div>
                          <div>
                            <label className={`text-[10px] block mb-1 ${textSub}`}>
                              {language === 'es' ? 'Holding Points' : 'Holding Points'}
                            </label>
                            <input
                              type="number"
                              min={0}
                              className={`w-full border rounded p-1.5 text-xs outline-none ${inputBg}`}
                              value={pair.holding_points || ''}
                              onChange={e => updateRunwayField(idx, 'holding_points', parseInt(e.target.value) || 0)}
                              onWheel={e => e.currentTarget.blur()}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* COLUMNA B — Annual Metrics (simplified) */}
              <div>
                <h3 className="text-sm font-bold mb-4 border-b pb-2 text-[var(--text-primary)] border-[var(--border)]">
                  {language === 'es' ? 'Métricas Anuales' : 'Annual Metrics'}
                </h3>

                <NumberInput label={language === 'es' ? 'TOTAL PASAJEROS ANUALES' : 'TOTAL ANNUAL PASSENGERS'} val={baseStats.annual_passengers} set={(v: number) => handlePaxChange(v)} placeholder={language === 'es' ? 'Ej: 45000000' : 'E.g.: 45000000'} unit="pax" />
                <NumberInput label={language === 'es' ? 'TOTAL MOVIMIENTOS ANUALES' : 'TOTAL ANNUAL MOVEMENTS'} val={baseStats.annual_movements} set={(v: number) => setBaseStats({ ...baseStats, annual_movements: v })} placeholder={language === 'es' ? 'Ej: 350000 (auto-estimado)' : 'E.g.: 350000 (auto-estimated)'} unit="movs" />

                {/* Auto-compute badges */}
                <div className={`mt-6 pt-4 border-t space-y-3 ${'border-[var(--border)]'}`}>
                  <h3 className={`text-sm font-bold mb-3 ${textSub}`}>
                    {language === 'es' ? 'Cálculo Automático' : 'Auto-Computed on Create'}
                  </h3>
                  <div className="p-3 rounded-lg border flex items-center gap-3"
                    style={{ background: 'color-mix(in srgb, var(--node-rrc) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--node-rrc) 30%, transparent)' }}>
                    <Zap className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--node-rrc)' }} />
                    <div>
                      <p className="text-xs font-bold" style={{ color: 'var(--node-rrc)' }}>RRC (Runway Capacity)</p>
                      <p className="text-[10px]" style={{ color: 'var(--node-rrc)', opacity: 0.75 }}>
                        {language === 'es' ? 'Calculado por RunCap (IATA ADRM) al crear' : 'Computed by RunCap (IATA ADRM) on create'}
                      </p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border flex items-center gap-3"
                    style={{ background: 'color-mix(in srgb, var(--node-ptbc) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--node-ptbc) 30%, transparent)' }}>
                    <Building2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--node-ptbc)' }} />
                    <div>
                      <p className="text-xs font-bold" style={{ color: 'var(--node-ptbc)' }}>PTBC (Terminal Capacity)</p>
                      <p className="text-[10px]" style={{ color: 'var(--node-ptbc)', opacity: 0.75 }}>
                        {language === 'es' ? 'Calculado por AirCap desde PAX anuales' : 'Computed by AirCap from annual PAX'}
                      </p>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border flex items-center gap-3"
                    style={{ background: 'color-mix(in srgb, var(--node-rac) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--node-rac) 30%, transparent)' }}>
                    <Settings2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--node-rac)' }} />
                    <div>
                      <p className="text-xs font-bold" style={{ color: 'var(--node-rac)' }}>CNS/ATM</p>
                      <p className="text-[10px]" style={{ color: 'var(--node-rac)', opacity: 0.75 }}>
                        {language === 'es'
                          ? (aipResult ? 'Auto-rellenado desde AIP' : 'Configurable en el panel de proyecto')
                          : (aipResult ? 'Auto-filled from AIP' : 'Configurable in project settings')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* D-FOL Inputs (manual — field observation from tower visits) */}
                <div className={`mt-6 pt-4 border-t ${'border-[var(--border)]'}`}>
                  <h3 className={`text-sm font-bold mb-2 ${textSub}`}>D-FOL Inputs</h3>
                  <p className="text-[10px] mb-4 text-[var(--text-tertiary)]">
                    {language === 'es'
                      ? 'Parámetros de observación de campo (Field Observation Log)'
                      : 'Field Observation Log parameters'}
                  </p>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className={`text-[10px] uppercase font-bold block mb-1 ${textSub}`}>W (min/op)</label>
                      <input type="number" step="0.01" className={`w-full border rounded p-2 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                        placeholder="2.74" value={dfolInputs.w_workload}
                        onChange={e => setDfolInputs({ ...dfolInputs, w_workload: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <label className={`text-[10px] uppercase font-bold block mb-1 ${textSub}`}>N_dynamic</label>
                      <input type="number" step="0.1" className={`w-full border rounded p-2 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                        placeholder="1.5" value={dfolInputs.n_dynamic}
                        onChange={e => setDfolInputs({ ...dfolInputs, n_dynamic: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <label className={`text-[10px] uppercase font-bold block mb-1 ${textSub}`}>T_sector (min)</label>
                      <input type="number" step="0.5" className={`w-full border rounded p-2 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                        placeholder="12.5" value={dfolInputs.t_sector}
                        onChange={e => setDfolInputs({ ...dfolInputs, t_sector: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* 3. WEATHER DATA (WCF) */}
          <div className={`p-6 rounded-xl border col-span-full ${cardBg}`}>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setMetarEnabled(!metarEnabled)}
            >
              <h2 className={`text-lg font-bold flex items-center gap-2 text-[var(--aipron-accent)]`}>
                <CloudSun className="w-5 h-5" />
                {language === 'es' ? 'Datos Meteorológicos (WCF)' : 'Weather Data (WCF)'}
              </h2>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${textSub}`}>
                  {language === 'es' ? 'Descargar METAR/TAF' : 'Download METAR/TAF'}
                </span>
                {metarEnabled ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>

            {metarEnabled && (
              <div className="mt-6 space-y-4">
                <p className={`text-xs ${textSub}`}>
                  {language === 'es'
                    ? 'Selecciona un rango de fechas para descargar los informes METAR y TAF. Estos datos se usarán para calcular el Weather Correction Factor (WCF) del análisis DACAMS.'
                    : 'Select a date range to download METAR and TAF reports. This data will be used to calculate the Weather Correction Factor (WCF) for the DACAMS analysis.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  {/* Start Date */}
                  <div>
                    <label className={`text-xs font-bold block mb-2 ${textSub}`}>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {language === 'es' ? 'FECHA INICIO' : 'START DATE'}
                    </label>
                    <input
                      type="date"
                      value={metarStartDate}
                      onChange={e => setMetarStartDate(e.target.value)}
                      className={`w-full border rounded-lg p-3 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className={`text-xs font-bold block mb-2 ${textSub}`}>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {language === 'es' ? 'FECHA FIN' : 'END DATE'}
                    </label>
                    <input
                      type="date"
                      value={metarEndDate}
                      onChange={e => setMetarEndDate(e.target.value)}
                      className={`w-full border rounded-lg p-3 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                    />
                  </div>

                  {/* Fetch Button */}
                  <div>
                    <button
                      type="button"
                      disabled={metarFetching || !icao || !metarStartDate || !metarEndDate}
                      onClick={handleFetchMetar}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold text-sm transition-all ${metarFetching || !icao || !metarStartDate || !metarEndDate
                        ? 'bg-[var(--surface-inset)] text-[var(--text-tertiary)] cursor-not-allowed'
                        : 'bg-[var(--aipron-info)] hover:brightness-90 text-white shadow-md'
                        }`}
                    >
                      {metarFetching ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> {language === 'es' ? 'Descargando...' : 'Downloading...'}</>
                      ) : (
                        <><Download className="w-4 h-4" /> {language === 'es' ? 'Obtener METAR/TAF' : 'Fetch METAR/TAF'}</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Progress */}
                {metarProgress && !metarResult && (
                  <div className="p-3 rounded-lg text-xs flex items-center gap-2"
                    style={isMetarError(metarProgress)
                      ? { background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-danger) 35%, transparent)', color: 'var(--aipron-danger)' }
                      : { background: 'color-mix(in srgb, var(--aipron-info) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-info) 35%, transparent)', color: 'var(--aipron-info)' }}>
                    {!isMetarError(metarProgress) && <Loader2 className="w-3 h-3 animate-spin" />}
                    {metarProgressText(metarProgress)}
                  </div>
                )}

                {/* Result */}
                {metarResult && (
                  <div className="p-4 rounded-lg border flex items-start gap-3"
                    style={{ background: 'color-mix(in srgb, var(--good) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--good) 30%, transparent)' }}>
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--good)' }} />
                    <div className="text-sm space-y-1">
                      <p className="font-bold" style={{ color: 'var(--good)' }}>
                        {language === 'es' ? 'Datos descargados correctamente' : 'Data downloaded successfully'}
                      </p>
                      <div className={`grid grid-cols-3 gap-4 text-xs ${textSub}`}>
                        <span><strong>METARs:</strong> {metarResult.metar_count.toLocaleString()}</span>
                        <span><strong>TAFs:</strong> {metarResult.taf_count.toLocaleString()}</span>
                        <span><strong>{language === 'es' ? 'Tamaño' : 'Size'}:</strong> {metarResult.raw_size_kb} KB</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. NOTAM CONFIGURATION */}
          <div className={`p-6 rounded-xl border col-span-full ${cardBg}`}>
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setNotamEnabled(!notamEnabled)}
            >
              <h2 className={`text-lg font-bold flex items-center gap-2 text-[var(--aipron-accent)]`}>
                <FileText className="w-5 h-5" />
                {language === 'es' ? 'Configuración NOTAM' : 'NOTAM Configuration'}
              </h2>
              <div className="flex items-center gap-3">
                <span className={`text-xs ${textSub}`}>
                  {language === 'es' ? 'Obtener NOTAMs via Notamify' : 'Fetch NOTAMs via Notamify'}
                </span>
                {notamEnabled ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>

            {notamEnabled && (
              <div className="mt-6 space-y-4">
                <p className={`text-xs ${textSub}`}>
                  {language === 'es'
                    ? 'Los NOTAMs se descargarán automáticamente al crear el proyecto. Si la descarga falla, el proyecto se creará igualmente.'
                    : 'NOTAMs will be fetched automatically on project creation. If the fetch fails, the project will still be created.'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className={`text-xs font-bold block mb-2 ${textSub}`}>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {language === 'es' ? 'FECHA INICIO NOTAM' : 'NOTAM START DATE'}
                    </label>
                    <input
                      type="date"
                      value={notamStartDate}
                      onChange={e => setNotamStartDate(e.target.value)}
                      className={`w-full border rounded-lg p-3 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label className={`text-xs font-bold block mb-2 ${textSub}`}>
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {language === 'es' ? 'FECHA FIN NOTAM' : 'NOTAM END DATE'}
                    </label>
                    <input
                      type="date"
                      value={notamEndDate}
                      onChange={e => setNotamEndDate(e.target.value)}
                      className={`w-full border rounded-lg p-3 text-sm focus:border-[var(--aipron-accent)] outline-none ${inputBg}`}
                    />
                  </div>
                </div>

                {/* Info banner */}
                <div className="p-3 rounded-lg text-xs flex items-start gap-2 border"
                  style={{ background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 30%, transparent)', color: 'var(--aipron-warning)' }}>
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-bold">{language === 'es' ? 'Nota:' : 'Note:'}</span>{' '}
                    {language === 'es'
                      ? `Los NOTAMs para ${icao || '----'} se obtendrán del ${notamStartDate || '---'} al ${notamEndDate || '---'} al pulsar "Crear Proyecto". Requiere API key de Notamify en VITE_NOTAMIFY_API_KEY.`
                      : `NOTAMs for ${icao || '----'} will be fetched from ${notamStartDate || '---'} to ${notamEndDate || '---'} when you click "Create Project". Requires Notamify API key in VITE_NOTAMIFY_API_KEY.`}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* NOTAM Toast Notification */}
          {notamToast && (
            <div className="col-span-full p-3 rounded-lg text-xs flex items-center justify-between border"
              style={{
                background: `color-mix(in srgb, ${notamToast.type === 'success' ? 'var(--good)' : notamToast.type === 'warning' ? 'var(--aipron-warning)' : 'var(--aipron-danger)'} 10%, transparent)`,
                borderColor: `color-mix(in srgb, ${notamToast.type === 'success' ? 'var(--good)' : notamToast.type === 'warning' ? 'var(--aipron-warning)' : 'var(--aipron-danger)'} 35%, transparent)`,
                color: notamToast.type === 'success' ? 'var(--good)' : notamToast.type === 'warning' ? 'var(--aipron-warning)' : 'var(--aipron-danger)',
              }}>
              <span>{notamToast.message}</span>
              <button type="button" onClick={() => setNotamToast(null)} className="ml-4 opacity-60 hover:opacity-100">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 6. DATASET (AODB) — upload + stateless pre-fill of the AirCap card */}
          <div className={`p-6 rounded-xl border col-span-full ${cardBg}`}>
            <h2 className={`text-lg font-bold mb-2 flex items-center gap-2 text-[var(--aipron-accent)]`}>
              <Database className="w-5 h-5" />
              {language === 'es' ? 'Dataset operativo (AODB)' : 'Operational dataset (AODB)'}
            </h2>
            <p className={`text-xs mb-4 ${textSub}`}>
              {language === 'es'
                ? 'Sube el Unified Dataset (.xlsx/.csv). Se analiza al instante para pre-rellenar la configuración de terminal (AirCap) más abajo, y se procesa al crear el proyecto. Opcional: puedes crear el proyecto y subirlo después.'
                : 'Upload the Unified Dataset (.xlsx/.csv). It is parsed instantly to pre-fill the terminal (AirCap) configuration below, and processed when you create the project. Optional: you can create the project and upload it later.'}
            </p>

            {/* Time basis (UTC / local) — same declaration as /{id}/upload */}
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${textSub}`}>
                {language === 'es' ? 'Base horaria del datasheet' : 'Datasheet time basis'}
              </span>
              <div className="inline-flex rounded-lg border overflow-hidden border-[var(--border)]">
                {(['utc', 'local'] as const).map((b) => (
                  <button key={b} type="button" onClick={() => setDatasetTimeBasis(b)}
                    className="px-3 py-1.5 text-xs font-semibold transition-colors"
                    style={{
                      background: datasetTimeBasis === b ? 'var(--accent-soft)' : 'transparent',
                      color: datasetTimeBasis === b ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                    }}>
                    {b === 'utc' ? 'UTC' : (language === 'es' ? 'Hora local' : 'Local time')}
                  </button>
                ))}
              </div>
            </div>

            {/* Dropzone */}
            <label
              className={`block border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all border-[var(--border)] hover:border-[var(--aipron-accent)] bg-[var(--surface-raised)] ${datasetParsing ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <input
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handleDatasetUpload(f);
                }}
              />
              {datasetParsing ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--aipron-accent)]" />
                  <span className={`text-sm ${textSub}`}>
                    {language === 'es' ? 'Analizando el dataset...' : 'Parsing the dataset...'}
                  </span>
                </div>
              ) : datasetFile && parseResult ? (
                <div className="flex flex-col items-center gap-1">
                  <Check className="w-6 h-6" style={{ color: 'var(--good)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--good)' }}>{datasetFile.name}</span>
                  <span className={`text-xs ${textSub}`}>
                    {language === 'es' ? 'Clic para reemplazar' : 'Click to replace'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className={`w-8 h-8 ${textSub}`} />
                  <span className={`text-sm font-medium ${textSub}`}>
                    {language === 'es' ? 'Arrastra el dataset o haz clic para seleccionar' : 'Drag the dataset or click to select'}
                  </span>
                  <span className={`text-[10px] ${textSub}`}>.XLSX · .CSV</span>
                </div>
              )}
            </label>

            {/* Parse error */}
            {datasetError && (
              <div className="mt-4 p-3 rounded-lg text-xs flex items-center gap-2"
                style={{ background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-danger) 35%, transparent)', color: 'var(--aipron-danger)' }}>
                <XCircle className="w-4 h-4 flex-shrink-0" />
                {datasetError}
              </div>
            )}

            {/* Sheets detected */}
            {parseResult && parseResult.sheets_detected?.length > 0 && (
              <div className="mt-4">
                <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${textSub}`}>
                  {language === 'es' ? 'Hojas detectadas' : 'Sheets detected'}
                </p>
                <div className="flex flex-wrap gap-1">
                  {parseResult.sheets_detected.map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full font-mono border"
                      style={{ background: 'color-mix(in srgb, var(--aipron-info) 12%, transparent)', color: 'var(--aipron-info)', borderColor: 'color-mix(in srgb, var(--aipron-info) 30%, transparent)' }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 7. AIRCAP TERMINAL + ADVANCED DACAMS PARAMS (auto-fill review) */}
          <ProjectAdvancedConfig
            value={advConfig}
            onChange={setAdvConfig}
            language={language}
            provenance={parseResult?.provenance}
          />

          {/* BARRA DE ACCIÓN */}
          <div className="col-span-full flex justify-end items-center mt-4 border-t pt-6 border-[var(--border)]">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="px-6 py-3 mr-4 rounded-lg text-sm font-medium transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-raised)]"
            >
              {language === 'es' ? 'Cancelar Operación' : 'Cancel Operation'}
            </button>
            <button
              type="submit"
              disabled={loading || !iata}
              className={`flex items-center gap-2 px-8 py-3 bg-[var(--aipron-accent)] hover:bg-[var(--aipron-accent-hover)] text-white font-bold rounded-lg shadow-lg transition-all transform active:scale-95 ${loading || !iata ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
            >
              {loading
                ? <>{language === 'es' ? 'Iniciando Motor...' : 'Starting Engine...'}</>
                : <><Save className="w-5 h-5" /> {language === 'es' ? 'Crear Proyecto' : 'Create Project'}</>}
            </button>
          </div>

        </form>

        {/* ═══════════ CONFIRMATION MODAL ═══════════ */}
        {showConfirmModal && pendingAirport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="w-full max-w-md mx-4 rounded-2xl border shadow-2xl p-8 bg-[var(--surface)] border-[var(--border)]">
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-[var(--accent-soft)] flex items-center justify-center">
                  <Plane className="w-8 h-8 text-[var(--aipron-accent)]" />
                </div>
              </div>

              {/* Title */}
              <h3 className={`text-xl font-bold text-center mb-2 ${textMain}`}>
                {language === 'es' ? '¿Confirmar aeropuerto?' : 'Confirm airport?'}
              </h3>

              {/* Airport info */}
              <div className={`text-center mb-6 ${textSub}`}>
                <p className={`text-lg font-bold ${'text-[var(--text-primary)]'}`}>
                  {pendingAirport.name}
                </p>
                <p className="text-sm font-mono mt-1">
                  {pendingAirport.iata} / {pendingAirport.icao}
                </p>
              </div>

              {/* Description */}
              <p className={`text-xs text-center mb-6 leading-relaxed ${textSub}`}>
                {language === 'es'
                  ? 'Al confirmar, el sistema buscará automáticamente el documento AIP AD2 del aeropuerto, lo descargará y analizará para rellenar la configuración del proyecto.'
                  : 'On confirmation, the system will automatically search for the airport\'s AIP AD2 document, download and analyze it to populate the project configuration.'}
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCancelAirport}
                  className="flex-1 py-3 rounded-xl text-sm font-bold transition-all bg-[var(--surface-inset)] hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]"
                >
                  {language === 'es' ? 'No, cambiar' : 'No, change'}
                </button>
                <button
                  type="button"
                  onClick={handleConfirmAirport}
                  className="flex-1 py-3 rounded-xl text-sm font-bold bg-[var(--aipron-accent)] hover:bg-[var(--aipron-accent-hover)] text-white transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  {language === 'es' ? 'Sí, buscar AIP' : 'Yes, search AIP'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div >
    </div >
  );
};